import { memo } from "react";
import { useTranslation } from "react-i18next";

import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { defaultNS } from "@/lib/i18n";

import { useForm } from "../../hooks/useForm";
import { useFormCollections } from "../../hooks/useForm/useFormCollections";
import type { FieldId, SelfServiceTaskForm } from "../../types";
import { AttributeCollection } from "../collections/AttributeCollection";

import { DynamicCollectionsContainer } from "./DynamicCollectionsContainer";

interface FormBaseProps {
  /** フォーム定義 */
  formDefinition: SelfServiceTaskForm;
  /** 読み取り専用モード */
  readOnly?: boolean;
  /** クラス名 */
  className?: string;
  /** テストID */
  "data-testid"?: string;
}

interface FormRendererProps extends FormBaseProps {
  /** レイアウト */
  layout?: "vertical" | "horizontal";
  /** エラー表示コンポーネント */
  ErrorComponent?: React.ComponentType<{
    errors: Array<{ fieldId: FieldId; message: string }>;
  }>;
}

/**
 * デフォルトのエラー表示コンポーネント
 */
const DefaultErrorComponent = ({
  errors,
}: {
  errors: Array<{ fieldId: FieldId; message: string }>;
}) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.formFactory.defaultErrorComponent",
  });
  return (
    <div
      className="border border-error-fill px-4 py-3 rounded relative mt-4"
      role="alert"
    >
      <Text variant="label" className="text-error-fill">
        {t("title")}
      </Text>
      <Text
        variant="labelSmall"
        className="mt-2 list-disc list-inside text-error-fill"
      >
        {errors.map((error, index) => (
          <li key={`${error.fieldId}-${index}`}>{error.message}</li>
        ))}
      </Text>
    </div>
  );
};

/**
 * フォームレンダラーコンポーネント
 *
 * フォーム定義に基づいて静的・動的コレクションを自動的に処理し表示する
 * API側の利用者は、このコンポーネントを使用するだけで、コレクションの種類を意識せずにフォームを表示できる
 *
 * このコンポーネントは、FormWrapperのchildrenが指定されていない場合に自動的に使用されます。
 * 通常は直接使用せず、FormWrapperを介して使用します。
 */
export const FormRenderer = memo(
  ({
    formDefinition,
    readOnly = false,
    className = "",
    layout = "vertical",
    "data-testid": testId = "form-renderer",
    ErrorComponent = DefaultErrorComponent,
  }: FormRendererProps) => {
    const { staticCollections, dynamicCollectionIds } =
      useFormCollections(formDefinition);

    const { errors, hasErrors } = useForm();

    if (!formDefinition.form?.attributeCollections?.length) {
      return null;
    }

    return (
      <div className={className} data-testid={testId}>
        {/* 静的コレクションの表示 */}
        {staticCollections.map((collection) => (
          <VStack key={`static-collection-${collection.id}-0`} className="mb-4">
            <AttributeCollection
              collectionId={collection.id}
              collectionIndex={0} // 静的コレクションは複数存在しないため、0固定
              readOnly={readOnly}
              layout={layout}
            />
          </VStack>
        ))}

        {/* 動的コレクションの表示 */}
        {dynamicCollectionIds.map((collectionId) => (
          <DynamicCollectionsContainer
            key={`dynamic-collections-${collectionId}`}
            collectionId={collectionId}
            readOnly={readOnly}
            layout={layout}
          />
        ))}

        {/* バリデーションエラーの表示 */}
        {hasErrors && errors.length > 0 && <ErrorComponent errors={errors} />}
      </div>
    );
  },
);

FormRenderer.displayName = "FormRenderer";
